import React from 'react';

import Form from 'mobx-react-form';
import { IconButton, InputAdornment, Stack, TableCell, TableRow, Tooltip } from '@mui/material';
import { Edit, Cancel } from '@mui/icons-material';

import { toNumber, isNumber } from 'lodash';

import NumericInput from 'components/shared/inputs/NumericInput';
import { RentLossItem } from 'types/underwriting/income-approach/cap-rate-conclusion';
import { getInputStyles } from './IncomeAdjustmentRow';

type RentLossTableRowProps = {
  form: Form;
  rowTitle: string;
  lossItem: RentLossItem;
  fieldPath: string;
  onEditItem: () => void;
  onRemoveItem: () => void;
};

const RentLossTableRow = ({ rowTitle, onEditItem, onRemoveItem, lossItem, fieldPath, form }: RentLossTableRowProps) => {
  const [isHovering, setIsHovering] = React.useState(false);

  const onMouseEnter = React.useCallback(() => {
    setIsHovering(true);
  }, []);
  const onMouseLeave = React.useCallback(() => {
    setIsHovering(false);
  }, []);

  const amountPrefix = lossItem.amount && lossItem.amount !== 0 ? '-$' : '$';

  const handleChange = (value: number) => {
    if (!isNumber(value)) {
      return;
    }

    try {
      const targetField = form.$(fieldPath);

      const monthlyLoss = targetField.value.monthlyLoss || 0;
      const amount = monthlyLoss * (value || 0);

      targetField.set({
        ...targetField.value,
        months: value,
        amount,
      });
    } catch (error) {
      console.error('Error setting field', error);
    }
  };

  return (
    <TableRow onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <TableCell>Less {rowTitle}</TableCell>
      <TableCell align="right">
        <NumericInput
          sx={{ ...getInputStyles(true) }}
          value={lossItem.months}
          onFocus={(event) => event.target?.select()}
          onChange={(value) => handleChange(toNumber(value))}
          allowNegative={false}
          fullWidth={false}
          placeholder="0"
          margin="none"
          thousandSeparator
          InputProps={{
            endAdornment: <InputAdornment position="end">months</InputAdornment>,
          }}
        />
      </TableCell>
      <TableCell align="right">
        <NumericInput
          sx={{ ...getInputStyles(true) }}
          value={lossItem.amount || 0}
          allowNegative={false}
          decimalScale={2}
          disabled={true}
          margin="none"
          placeholder="0.00"
          prefix={amountPrefix}
          thousandSeparator
        />
      </TableCell>
      <TableCell />
      <TableCell>
        {isHovering && (
          <Stack alignItems="center" direction="row" justifyContent="end" spacing={2} sx={{ my: -1 }}>
            <Tooltip title="Edit Loss Item" placement="top">
              <IconButton onClick={onEditItem} size="small">
                <Edit color="info" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove Loss Item" placement="top">
              <IconButton onClick={onRemoveItem} size="small">
                <Cancel color="error" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </TableCell>
    </TableRow>
  );
};

export default RentLossTableRow;
